/**
 * Torn calculators for the website (pure, browser-safe): the merit planner, the respect calculator and what a bank
 * investment earns. Torn's numbers are from its own published pages, as cited by each.
 */

// ---------------------------------------------------------------- merits

/**
 * Merit upgrades and their effect per level — Torn wiki "Merit" (wiki.torn.com/wiki/Merit, read 2026-10-29). Every upgrade
 * has 10 levels; level n costs n merits (1 + 2 + … + 10 = 55 to max one).
 */
export interface MeritDef {
  id: string;
  name: string;
  group: 'Battle' | 'Weapons' | 'General';
  /** Effect per level (amount, unit) — "+3% strength" is { per: 3, unit: '% strength' }. */
  per: number;
  unit: string;
}

export const MERIT_MAX_LEVEL = 10;

export const MERITS: readonly MeritDef[] = [
  { id: 'brawn', name: 'Brawn', group: 'Battle', per: 3, unit: '% strength' },
  { id: 'protection', name: 'Protection', group: 'Battle', per: 3, unit: '% defense' },
  { id: 'sharpness', name: 'Sharpness', group: 'Battle', per: 3, unit: '% speed' },
  { id: 'evasion', name: 'Evasion', group: 'Battle', per: 3, unit: '% dexterity' },
  { id: 'critical', name: 'Critical Hit Rate', group: 'Battle', per: 0.5, unit: '% critical hit rate' },
  { id: 'life', name: 'Life Points', group: 'Battle', per: 5, unit: '% maximum life' },
  { id: 'stealth', name: 'Stealth', group: 'Battle', per: 0.2, unit: ' stealth level' },
  { id: 'hospitalizing', name: 'Hospitalizing', group: 'Battle', per: 5, unit: '% hospital time you give' },
  ...(
    [
      ['heavy', 'Heavy Artillery Mastery'],
      ['machinegun', 'Machine Gun Mastery'],
      ['rifle', 'Rifle Mastery'],
      ['smg', 'SMG Mastery'],
      ['shotgun', 'Shotgun Mastery'],
      ['pistol', 'Pistol Mastery'],
      ['club', 'Club Mastery'],
      ['piercing', 'Piercing Mastery'],
      ['slashing', 'Slashing Mastery'],
      ['mechanical', 'Mechanical Mastery'],
      ['temporary', 'Temporary Mastery'],
    ] as const
  ).map(([id, name]): MeritDef => ({ id, name, group: 'Weapons', per: 1, unit: '% damage, +0.2 accuracy' })),
  { id: 'nerve', name: 'Nerve Bar', group: 'General', per: 1, unit: ' maximum nerve' },
  { id: 'crimeprog', name: 'Crime Progression', group: 'General', per: 1, unit: '% crime experience and skill gain' },
  { id: 'education', name: 'Education Length', group: 'General', per: -2, unit: '% course time' },
  { id: 'awareness', name: 'Awareness', group: 'General', per: 20, unit: '% items found in the city' },
  { id: 'bank', name: 'Bank Interest', group: 'General', per: 5, unit: '% bank interest' },
  { id: 'looting', name: 'Masterful Looting', group: 'General', per: 5, unit: '% money from mugging' },
  { id: 'addiction', name: 'Addiction Mitigation', group: 'General', per: -2, unit: '% addiction effects' },
  { id: 'employee', name: 'Employee Effectiveness', group: 'General', per: 1, unit: ' employee effectiveness' },
];

/** Merits it takes to go from level `from` to level `to` (level n costs n). */
export function meritCost(from: number, to: number): number {
  const a = clampLevel(from);
  const b = clampLevel(to);
  if (b <= a) return 0;
  return (b * (b + 1)) / 2 - (a * (a + 1)) / 2;
}

const clampLevel = (n: number): number => Math.max(0, Math.min(MERIT_MAX_LEVEL, Math.floor(Number.isFinite(n) ? n : 0)));

/** "+15% strength" for a merit at a level (0 → ""). */
export function meritEffect(m: Pick<MeritDef, 'per' | 'unit'>, level: number): string {
  const v = Math.round(m.per * clampLevel(level) * 100) / 100;
  if (!v) return '';
  return `${v > 0 ? '+' : '−'}${Math.abs(v)}${m.unit}`;
}

export interface MeritPlan {
  /** Merits the plan spends beyond what's already bought. */
  cost: number;
  /** Merits left (null: no budget given). */
  left: number | null;
  /** Everything bought in the plan, from scratch. */
  total: number;
}

/** A plan: current and wanted level per merit id, and the merits there are to spend. */
export function planMerits(current: Record<string, number>, wanted: Record<string, number>, available: number | null): MeritPlan {
  let cost = 0;
  let total = 0;
  for (const m of MERITS) {
    const now = clampLevel(current[m.id] ?? 0);
    const want = Math.max(now, clampLevel(wanted[m.id] ?? now));
    cost += meritCost(now, want);
    total += meritCost(0, want);
  }
  return { cost, left: available === null ? null : available - cost, total };
}

/** The highest level reachable from `from` with `merits` to spend. */
export function maxLevelWith(from: number, merits: number): number {
  let lvl = clampLevel(from);
  let left = merits;
  while (lvl < MERIT_MAX_LEVEL && left >= lvl + 1) {
    left -= lvl + 1;
    lvl++;
  }
  return lvl;
}

// ---------------------------------------------------------------- respect

/**
 * Respect for one attack — Torn wiki "Chain" (wiki.torn.com/wiki/Chain, read 2026-10-29):
 *  - base respect = (1/198)·level + 197/198, rounded to two decimals (≈ level/200 + 1);
 *  - × fair fight (1 to 3: 3 when the target has about 75% of your battle stats or more);
 *  - × chain scale 0.25·log10(hit) + 0.75 for hits after the 10th (not on bonus hits);
 *  - × 2 against a faction you're at war with; × 1.5 retaliation; × 1.25 overseas; × 0.75 when you mug;
 *  - × group attack 1.5 − 0.25·0.5^(n−1) for n other faction members who joined (none: × 1);
 *  - bonus hits (10, 25, 50 … 100,000) give a fixed amount instead (10, 20, 40 … 40,960).
 */
export const CHAIN_BONUS: ReadonlyArray<[hit: number, respect: number]> = [
  [10, 10],
  [25, 20],
  [50, 40],
  [100, 80],
  [250, 160],
  [500, 320],
  [1000, 640],
  [2500, 1280],
  [5000, 2560],
  [10000, 5120],
  [25000, 10240],
  [50000, 20480],
  [100000, 40960],
];

export function baseRespect(level: number): number {
  const l = Math.max(1, Math.min(100, Math.floor(level)));
  return Math.round(((1 / 198) * l + 197 / 198) * 100) / 100;
}

export function chainScale(hit: number): number {
  return hit > 10 ? 0.25 * Math.log10(hit) + 0.75 : 1;
}

export function groupMultiplier(helpers: number): number {
  return helpers > 0 ? 1.5 - 0.25 * 0.5 ** (helpers - 1) : 1;
}

/**
 * Fair fight from both battle stat scores (the sum of the square roots of the four stats): 1 + 8/3 × (target ÷ you),
 * between 1 and 3 — FF Scouter's "Fair Fight explained" (ffscouter.com/guides/fair-fight-explained), which matches Torn's.
 */
export function fairFight(targetScore: number, yourScore: number): number {
  if (!(yourScore > 0) || !(targetScore >= 0)) return 1;
  return Math.max(1, Math.min(3, 1 + (8 / 3) * (targetScore / yourScore)));
}

/** Battle stat score: Σ √stat. */
export const battleScore = (stats: readonly number[]): number => stats.reduce((s, x) => s + Math.sqrt(Math.max(0, x)), 0);

export interface RespectInput {
  level: number;
  fairFight: number;
  /** Which chain hit this is (1 = the first, or no chain). */
  hit: number;
  war?: boolean;
  retaliation?: boolean;
  overseas?: boolean;
  mug?: boolean;
  /** Other faction members who joined the attack. */
  helpers?: number;
}

export interface RespectResult {
  respect: number;
  base: number;
  /** A bonus hit: its fixed respect, nothing else applies. */
  bonus: number | null;
  /** Each multiplier that applied, in order. */
  parts: Array<{ label: string; x: number }>;
}

export function respectFor(i: RespectInput): RespectResult {
  const base = baseRespect(i.level);
  const hit = Math.max(1, Math.floor(i.hit || 1));
  const bonus = CHAIN_BONUS.find(([h]) => h === hit)?.[1] ?? null;
  if (bonus !== null) return { respect: bonus, base, bonus, parts: [] };
  const ff = Math.max(1, Math.min(3, Number.isFinite(i.fairFight) ? i.fairFight : 1));
  const parts: RespectResult['parts'] = [{ label: 'Fair fight', x: ff }];
  if (hit > 10) parts.push({ label: `Chain hit ${hit.toLocaleString('en-US')}`, x: chainScale(hit) });
  if (i.war) parts.push({ label: 'War', x: 2 });
  if (i.retaliation) parts.push({ label: 'Retaliation', x: 1.5 });
  if (i.helpers && i.helpers > 0) parts.push({ label: `Group attack (${i.helpers} joined)`, x: groupMultiplier(i.helpers) });
  if (i.overseas) parts.push({ label: 'Overseas', x: 1.25 });
  if (i.mug) parts.push({ label: 'Mugged', x: 0.75 });
  const respect = parts.reduce((r, p) => r * p.x, base);
  return { respect: Math.round(respect * 100) / 100, base, bonus: null, parts };
}

/** A whole chain of `hits` the same way (hits 1…n, bonus hits included): its total respect. */
export function chainTotal(i: Omit<RespectInput, 'hit'>, hits: number): { total: number; bonus: number } {
  let total = 0;
  let bonus = 0;
  const n = Math.max(0, Math.min(100_000, Math.floor(hits)));
  for (let h = 1; h <= n; h++) {
    const r = respectFor({ ...i, hit: h });
    total += r.respect;
    if (r.bonus !== null) bonus += r.bonus;
  }
  return { total: Math.round(total * 100) / 100, bonus };
}

// ---------------------------------------------------------------- bank

/** Torn's bank: at most $2,000,000,000 invested. */
export const BANK_MAX = 2_000_000_000;

/**
 * What an investment earns: the yearly rate × (1 + 5% per Bank Interest merit level) × (1 + 10% with the Torn City
 * Investments company's perk) over the days — the way Torn's bank page works it out (and TornTools' bank info).
 */
export function bankEarnings(amount: number, ratePercent: number, days: number, opts: { merits?: number; tci?: boolean } = {}): { total: number; perDay: number; rate: number } {
  const merits = Math.max(0, Math.min(10, Math.floor(opts.merits ?? 0)));
  const rate = ratePercent * (1 + 0.05 * merits) * (opts.tci ? 1.1 : 1);
  const a = Math.max(0, Math.min(BANK_MAX, amount || 0));
  const total = Math.round(((a * rate) / 100 / 365) * days);
  return { total, perDay: days > 0 ? Math.round(total / days) : 0, rate };
}

// ---------------------------------------------------------------- Elo

/**
 * Torn's attack Elo — Torn wiki "Elo" (wiki.torn.com/wiki/Elo, read 2026-10-30): everyone starts at 1,000; a win gains,
 * a loss loses, a stalemate changes nothing; equal ratings move 5 either way; at most 10, at least 0, the most at a gap
 * of 512. That is the classic Elo formula with K = 10, rounded to whole points: the wiki's own table of gains by gap
 * (−512 or lower: 0 … −34 to +34: 5 … +512 or higher: 10) comes out of it exactly.
 */
export const ELO_START = 1000;
export const ELO_K = 10;

/** Your chance of winning by Elo's reckoning (0…1). */
export const eloExpected = (you: number, opponent: number): number => 1 / (1 + 10 ** ((opponent - you) / 400));

/** What a win gains and a loss costs you against `opponent` (whole points, 0…10). */
export function eloChange(you: number, opponent: number): { win: number; loss: number } {
  const e = eloExpected(you, opponent);
  return { win: Math.round(ELO_K * (1 - e)), loss: Math.round(ELO_K * e) };
}

/**
 * The breakdown for your Elo: for each gain a win can bring (0…10), the opponents' Elo that gives it (`from`/`to`
 * inclusive; null = no limit that way), and what a loss to them costs. Gaps are whole points.
 */
export function eloBreakdown(you: number): Array<{ win: number; loss: number; from: number | null; to: number | null; gapFrom: number | null; gapTo: number | null }> {
  const r = Math.round(you);
  const out: Array<{ win: number; loss: number; from: number | null; to: number | null; gapFrom: number | null; gapTo: number | null }> = [];
  const SPAN = 1200; // well past ±512, where the gain stops changing
  for (let gap = -SPAN; gap <= SPAN; gap++) {
    const c = eloChange(r, r + gap);
    const last = out[out.length - 1];
    if (last && last.win === c.win) last.gapTo = gap;
    else out.push({ win: c.win, loss: c.loss, from: null, to: null, gapFrom: gap, gapTo: gap });
  }
  out[0]!.gapFrom = null;
  out[out.length - 1]!.gapTo = null;
  for (const row of out) {
    row.from = row.gapFrom === null ? null : r + row.gapFrom;
    row.to = row.gapTo === null ? null : r + row.gapTo;
  }
  return out;
}
